/**
 * useCountUp — animate a number from 0 (or a previous value) to the target.
 *
 * Cheap, dependency-free. RAF-driven. Honors reduced-motion (snaps instantly).
 * Used for score reveals on the Affirmation card and inside games.
 */
import { useEffect, useRef, useState } from 'react';

import { easing } from './easing';
import { prefersReducedMotion, timings } from './timings';

export interface UseCountUpOptions {
  /** Target value to count to. */
  to: number;
  /** Where the count starts. Default 0. */
  from?: number;
  /** Duration in ms. Default `timings.celebrate`. */
  durationMs?: number;
  /** Easing tuple for the curve. Default outQuart. */
  ease?: readonly [number, number, number, number];
}

/** Returns the currently-animated value. Re-runs whenever `to` changes. */
export function useCountUp({
  to,
  from = 0,
  durationMs = timings.celebrate,
  ease = easing.outQuart.tuple,
}: UseCountUpOptions): number {
  const [value, setValue] = useState<number>(from);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(to);
      return;
    }

    const start = performance.now();
    const delta = to - from;
    const [p1x, p1y, p2x, p2y] = ease;

    const tick = (now: number): void => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / Math.max(1, durationMs));
      const eased = cubicBezier(t, p1x, p1y, p2x, p2y);
      setValue(Math.round(from + delta * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [to, from, durationMs, ease]);

  return value;
}

/**
 * Sample a cubic-bezier(p1x, p1y, p2x, p2y) at t∈[0,1] using Newton's method
 * to invert x(t). Off-the-shelf utility — same algorithm WebKit ships.
 */
function cubicBezier(
  t: number,
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;

  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;

  const sampleX = (u: number): number => ((ax * u + bx) * u + cx) * u;
  const sampleY = (u: number): number => ((ay * u + by) * u + cy) * u;
  const slopeX = (u: number): number => (3 * ax * u + 2 * bx) * u + cx;

  // Newton-Raphson — usually converges in 4 iters.
  let u = t;
  for (let i = 0; i < 8; i += 1) {
    const x = sampleX(u) - t;
    if (Math.abs(x) < 1e-5) break;
    const dx = slopeX(u);
    if (Math.abs(dx) < 1e-6) break;
    u -= x / dx;
  }
  return sampleY(u);
}
